import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Building2, Users, Shield, ShieldCheck, TrendingUp, AlertTriangle,
  CheckCircle, XCircle, Clock, BarChart3, Eye, ArrowRight, Search, Trash2, Settings,
  KeyRound, Mail, UserCog, Lock, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { AddOrganizationDialog } from '../components/AddOrganizationDialog';

export default function SuperAdminDashboard() {
  const { user, selectOrg } = useContext(AuthContext);
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [organizations, setOrganizations] = useState([]);
  const [showAddOrg, setShowAddOrg] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  
  // Framework management state
  const [showFrameworkDialog, setShowFrameworkDialog] = useState(false);
  const [selectedOrgForFrameworks, setSelectedOrgForFrameworks] = useState(null);
  const [frameworkSettings, setFrameworkSettings] = useState(null);
  const [savingFrameworks, setSavingFrameworks] = useState(false);

  // User management state
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [selectedOrgForUsers, setSelectedOrgForUsers] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isSuperAdmin]);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/super-admin/organizations`);
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOrg = (org) => {
    selectOrg(org);
    navigate('/');
  };

  const openDeleteDialog = (org, e) => {
    e.stopPropagation(); // Prevent card click
    setOrgToDelete(org);
    setConfirmText('');
    setShowDeleteDialog(true);
  };

  const handleDeleteOrg = async () => {
    if (!orgToDelete || confirmText !== orgToDelete.name) {
      toast.error('Please type the organization name to confirm deletion');
      return;
    }
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/super-admin/organizations/${orgToDelete.id}`);
      toast.success(response.data.message);
      setShowDeleteDialog(false);
      setOrgToDelete(null);
      setConfirmText('');
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organization');
    } finally {
      setDeleting(false);
    }
  };

  // Framework management functions
  const openFrameworkDialog = async (org, e) => {
    e.stopPropagation();
    setSelectedOrgForFrameworks(org);
    setShowFrameworkDialog(true);
    
    try {
      const response = await axios.get(`${API}/admin/tenants/${org.id}/frameworks`);
      setFrameworkSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch framework settings:', error);
      toast.error('Failed to load framework settings');
    }
  };

  const toggleFramework = (frameworkId) => {
    if (!frameworkSettings) return;
    
    const updatedFrameworks = frameworkSettings.frameworks.map(fw => {
      if (fw.id === frameworkId) {
        return { ...fw, enabled: !fw.enabled };
      }
      return fw;
    });
    
    setFrameworkSettings({
      ...frameworkSettings,
      frameworks: updatedFrameworks,
      enabled_count: updatedFrameworks.filter(fw => fw.enabled).length
    });
  };

  const saveFrameworkSettings = async () => {
    if (!frameworkSettings || !selectedOrgForFrameworks) return;
    
    const enabledFrameworksList = frameworkSettings.frameworks
      .filter(fw => fw.enabled)
      .map(fw => fw.id);
    
    if (enabledFrameworksList.length === 0) {
      toast.error('At least one framework must be enabled');
      return;
    }
    
    setSavingFrameworks(true);
    try {
      await axios.put(`${API}/admin/tenants/${selectedOrgForFrameworks.id}/frameworks`, {
        enabled_frameworks: enabledFrameworksList
      });
      toast.success(`Framework settings updated for ${selectedOrgForFrameworks.name}`);
      setShowFrameworkDialog(false);
      setFrameworkSettings(null);
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save framework settings');
    } finally {
      setSavingFrameworks(false);
    }
  };

  // User management functions
  const openUsersDialog = async (org, e) => {
    e.stopPropagation();
    setSelectedOrgForUsers(org);
    setShowUsersDialog(true);
    setLoadingUsers(true);
    setResetPasswordUserId(null);
    setNewPassword('');
    try {
      const response = await axios.get(`${API}/super-admin/organizations/${org.id}`);
      setOrgUsers(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setResettingPassword(true);
    try {
      await axios.post(`${API}/super-admin/reset-password`, {
        user_id: userId,
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setResetPasswordUserId(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const getComplianceColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getComplianceBgColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-100 border-green-200';
    if (percentage >= 50) return 'bg-yellow-100 border-yellow-200';
    return 'bg-red-100 border-red-200';
  };

  const CHART_COLORS = ['#22c55e', '#ef4444', '#eab308', '#94a3b8'];

  // Calculate totals across all organizations
  const totals = organizations.reduce((acc, org) => {
    acc.totalOrgs += 1;
    acc.totalUsers += org.user_count || 0;
    acc.totalControls += org.compliance_stats?.total_controls || 0;
    acc.totalCompliant += org.compliance_stats?.compliant || 0;
    acc.totalNonCompliant += org.compliance_stats?.non_compliant || 0;
    return acc;
  }, { totalOrgs: 0, totalUsers: 0, totalControls: 0, totalCompliant: 0, totalNonCompliant: 0 });

  const overallComplianceRate = totals.totalCompliant + totals.totalNonCompliant > 0 
    ? Math.round((totals.totalCompliant / (totals.totalCompliant + totals.totalNonCompliant)) * 100) 
    : 0;

  // Filter organizations based on search
  const filteredOrganizations = organizations.filter(org =>
    org.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-700">
            This page is only accessible to Super Administrators.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-lg text-gray-600">Loading organizations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-purple-600" />
            <h1 className="text-3xl font-heading font-bold">Organizations Overview</h1>
          </div>
          <p className="text-gray-600 mt-2">
            Select an organization to view their compliance dashboard
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => setShowAddOrg(true)} data-testid="add-organization-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Organization
          </Button>
          <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
            <ShieldCheck className="w-4 h-4" />
            Super Admin
          </div>
        </div>
      </div>
      <AddOrganizationDialog open={showAddOrg} onOpenChange={setShowAddOrg} onCreated={fetchOrganizations} />

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <Building2 className="w-4 h-4" />
            <span className="text-sm">Organizations</span>
          </div>
          <p className="text-3xl font-bold">{totals.totalOrgs}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-sm">Total Users</span>
          </div>
          <p className="text-3xl font-bold">{totals.totalUsers}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <BarChart3 className="w-4 h-4" />
            <span className="text-sm">Total Controls</span>
          </div>
          <p className="text-3xl font-bold">{totals.totalControls}</p>
        </div>
        <div className="bg-white border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Compliant</span>
          </div>
          <p className="text-3xl font-bold text-green-600">{totals.totalCompliant}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Overall Compliance</span>
          </div>
          <p className={`text-3xl font-bold ${getComplianceColor(overallComplianceRate)}`}>
            {overallComplianceRate}%
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search organizations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
      </div>

      {/* Organizations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredOrganizations.map((org) => {
          const stats = org.compliance_stats || {};
          const chartData = [
            { name: 'Compliant', value: stats.compliant || 0 },
            { name: 'Non-Compliant', value: stats.non_compliant || 0 },
            { name: 'Partially Compliant', value: stats.partial || 0 },
            { name: 'Not Assessed', value: stats.not_assessed || 0 }
          ].filter(d => d.value > 0);
          
          return (
            <div 
              key={org.id} 
              className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-lg hover:border-purple-300 transition-all cursor-pointer group"
              onClick={() => handleSelectOrg(org)}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-slate-900 group-hover:text-purple-700">
                    {org.name}
                  </h3>
                  <p className="text-sm text-slate-500 capitalize">{org.subscription_plan}</p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-bold ${getComplianceBgColor(stats.compliance_percentage || 0)}`}>
                  <span className={getComplianceColor(stats.compliance_percentage || 0)}>
                    {stats.compliance_percentage || 0}%
                  </span>
                </div>
              </div>

              {/* Stats Row */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-900">{org.user_count}</p>
                    <p className="text-xs text-slate-500">Users</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-900">{stats.total_controls || 0}</p>
                    <p className="text-xs text-slate-500">Controls</p>
                  </div>
                </div>
                
                {/* Mini Pie Chart */}
                <div className="w-16 h-16">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={chartData.length > 0 ? chartData : [{ name: 'Empty', value: 1 }]}
                        cx="50%"
                        cy="50%"
                        innerRadius={15}
                        outerRadius={25}
                        dataKey="value"
                      >
                        {chartData.length > 0 ? (
                          chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))
                        ) : (
                          <Cell fill="#e2e8f0" />
                        )}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="flex gap-3 text-xs mb-4">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  {stats.compliant || 0}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  {stats.non_compliant || 0}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  {stats.partial || 0}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                  {stats.not_assessed || 0}
                </span>
              </div>

              {/* Frameworks */}
              <div className="flex flex-wrap gap-1 mb-4">
                {(Array.isArray(org.enabled_frameworks) ? org.enabled_frameworks : Object.keys(org.enabled_frameworks || {}).filter(k => org.enabled_frameworks[k])).slice(0, 4).map(fw => (
                  <span key={fw} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                    {fw === 'ISO27001' ? 'ISO 27001' : 
                     fw === 'SOC2' ? 'SOC 2' : 
                     fw === 'Essential8' ? 'Essential 8' : 
                     fw === 'PCIDSS' ? 'PCI DSS' : 
                     fw === 'ISO42001' ? 'ISO 42001' : fw}
                  </span>
                ))}
                {(() => {
                  const fwList = Array.isArray(org.enabled_frameworks) ? org.enabled_frameworks : Object.keys(org.enabled_frameworks || {}).filter(k => org.enabled_frameworks[k]);
                  return fwList.length > 4 ? (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                      +{fwList.length - 4}
                    </span>
                  ) : null;
                })()}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1 group-hover:bg-purple-600 group-hover:text-white group-hover:border-purple-600"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline"
                  className="text-blue-600 border-blue-300 hover:bg-blue-50 hover:border-blue-400"
                  onClick={(e) => openUsersDialog(org, e)}
                  title="Manage Users"
                  data-testid={`manage-users-btn-${org.id}`}
                >
                  <Users className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  className="text-slate-600 border-slate-300 hover:bg-slate-50 hover:border-slate-400"
                  onClick={(e) => openFrameworkDialog(org, e)}
                  title="Manage Frameworks"
                >
                  <Settings className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                  onClick={(e) => openDeleteDialog(org, e)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredOrganizations.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>{searchTerm ? 'No organizations match your search' : 'No organizations found'}</p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Organization
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              This action cannot be undone. This will permanently delete the organization
              <span className="font-semibold text-slate-900"> "{orgToDelete?.name}"</span> and remove all associated data including:
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-4">
            <ul className="text-sm text-slate-700 space-y-1">
              <li>• All users in the organization</li>
              <li>• All compliance controls and status</li>
              <li>• All risks, incidents, and policies</li>
              <li>• All assets, suppliers, and documents</li>
              <li>• All audit logs and invites</li>
            </ul>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-slate-700">
              Type <span className="font-semibold">"{orgToDelete?.name}"</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Enter organization name"
            />
          </div>

          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteOrg}
              disabled={confirmText !== orgToDelete?.name || deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Organization'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Framework Management Dialog */}
      <Dialog open={showFrameworkDialog} onOpenChange={setShowFrameworkDialog}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Settings className="w-5 h-5 text-purple-600" />
              Manage Frameworks
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Enable or disable compliance frameworks for 
              <span className="font-semibold text-slate-900"> {selectedOrgForFrameworks?.name}</span>
            </DialogDescription>
          </DialogHeader>
          
          {frameworkSettings ? (
            <>
              <div className="space-y-3 my-4">
                {frameworkSettings.frameworks.map((fw) => (
                  <div 
                    key={fw.id}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      fw.enabled 
                        ? 'bg-green-50 border-green-200 hover:bg-green-100' 
                        : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                    }`}
                    onClick={() => toggleFramework(fw.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        fw.enabled ? 'bg-green-500' : 'bg-slate-300'
                      }`}>
                        <Shield className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{fw.name}</p>
                        <p className="text-xs text-slate-500">{fw.description}</p>
                      </div>
                    </div>
                    <div className={`w-12 h-6 rounded-full transition-all ${
                      fw.enabled ? 'bg-green-500' : 'bg-slate-300'
                    } flex items-center ${fw.enabled ? 'justify-end' : 'justify-start'} px-1`}>
                      <div className="w-4 h-4 bg-white rounded-full shadow"></div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
                <span>{frameworkSettings.enabled_count} of {frameworkSettings.total_count} frameworks enabled</span>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowFrameworkDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={saveFrameworkSettings}
                  disabled={savingFrameworks || frameworkSettings.enabled_count === 0}
                >
                  {savingFrameworks ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-slate-500">
              Loading framework settings...
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Users Management Dialog */}
      <Dialog open={showUsersDialog} onOpenChange={(open) => { setShowUsersDialog(open); if (!open) { setResetPasswordUserId(null); setNewPassword(''); } }}>
        <DialogContent className="max-w-2xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Users className="w-5 h-5 text-blue-600" />
              Users — {selectedOrgForUsers?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {orgUsers.length} user{orgUsers.length !== 1 ? 's' : ''} in this organization
            </DialogDescription>
          </DialogHeader>
          
          {loadingUsers ? (
            <div className="py-8 text-center text-slate-500">Loading users...</div>
          ) : orgUsers.length === 0 ? (
            <div className="py-8 text-center text-slate-400">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No users in this organization</p>
            </div>
          ) : (
            <div className="space-y-3 mt-2" data-testid="users-list">
              {orgUsers.map((u) => (
                <div key={u.id} className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <UserCog className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate" data-testid={`user-name-${u.id}`}>{u.name || 'Unnamed'}</p>
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate" data-testid={`user-email-${u.id}`}>{u.email}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {u.role}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                        onClick={() => { setResetPasswordUserId(resetPasswordUserId === u.id ? null : u.id); setNewPassword(''); }}
                        data-testid={`reset-password-btn-${u.id}`}
                      >
                        <KeyRound className="w-3.5 h-3.5 mr-1" />
                        Reset Password
                      </Button>
                    </div>
                  </div>
                  
                  {/* Inline password reset form */}
                  {resetPasswordUserId === u.id && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2" data-testid={`reset-password-form-${u.id}`}>
                      <Lock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <Input
                        type="text"
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="flex-1 h-9 text-sm"
                        data-testid={`new-password-input-${u.id}`}
                      />
                      <Button
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-700 text-white h-9 px-4"
                        onClick={() => handleResetPassword(u.id)}
                        disabled={resettingPassword || !newPassword}
                        data-testid={`confirm-reset-btn-${u.id}`}
                      >
                        {resettingPassword ? 'Resetting...' : 'Confirm'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9"
                        onClick={() => { setResetPasswordUserId(null); setNewPassword(''); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}

                  <div className="mt-2 text-xs text-slate-400">
                    Created: {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                    {u.mfa_enabled && <span className="ml-2 text-green-600 font-medium">MFA Enabled</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
